import { useMutation } from '@tanstack/react-query';
import type { QuoteItemDTO } from '@setvi/shared/services/react-query';
import {
  fetchProductsPricing,
  PricingType
} from '@setvi/shared/services/react-query';
import {
  extractItems,
  buildQuoteRequestBody,
  updateItemsWithPricing,
  mapQuoteItems,
  getItemId
} from '../utils/quote-helpers';
import { useQuoteCache } from './use-quote-cache';
import { useQueuedUpdateMutation } from './use-queued-update-mutation';

interface RefreshSinglePriceParams {
  productId: string;
  sku: string;
}

/**
 * Mark a specific product as needing pricing update
 */
const markProductForPricing = (
  items: QuoteItemDTO[],
  productId: string
): QuoteItemDTO[] =>
  mapQuoteItems(items, product => {
    if (getItemId(product) === productId && !product.isFreeHand) {
      return { ...product, isPricingFetching: true };
    }
    return product;
  });

/**
 * Clear pricing fetching state from all products
 */
export const clearPricingFetchingState = (
  items: QuoteItemDTO[]
): QuoteItemDTO[] =>
  mapQuoteItems(items, product => ({
    ...product,
    isPricingFetching: false
  }));

/**
 * Refresh price for a single product in the quote
 * 1. Marks the specific product as fetching (optimistic update in onMutate)
 * 2. Fetches latest pricing from API for that product
 * 3. Updates the product with new pricing data
 * 4. Saves complete quote
 */
export const useRefreshSinglePrice = (quoteId: string) => {
  const cache = useQuoteCache(quoteId);
  const {
    mutateAsync: updateQuote,
    hasQueuedMutations,
    setOnQueueEmpty
  } = useQueuedUpdateMutation();

  return useMutation({
    mutationFn: async ({ sku }: RefreshSinglePriceParams) => {
      await cache.cancel();

      // Get quote from cache (product already marked for pricing by onMutate)
      const currentQuote = cache.getQuote();
      if (!currentQuote) {
        throw new Error('Quote not found in cache');
      }

      // Create a copy to update with pricing
      const updatedQuote = { ...currentQuote };

      const accountNumber = currentQuote.account?.accountNumber;
      const segmentId = currentQuote.segment?._id;

      const pricingResponse = await fetchProductsPricing({
        accountNumber: accountNumber ?? segmentId,
        products: [{ sku, quantity: 1 }],
        type: PricingType.Quote
      });

      if (pricingResponse.pricing?.length > 0) {
        updatedQuote.items = updateItemsWithPricing(
          updatedQuote.items,
          pricingResponse.pricing
        );

        updatedQuote.items = clearPricingFetchingState(updatedQuote.items);
      } else {
        updatedQuote.items = clearPricingFetchingState(updatedQuote.items);
      }

      // Update cache immediately to clear loading state in UI
      cache.setQuote(() => updatedQuote);

      const items = extractItems(updatedQuote.items);

      return updateQuote({
        quoteId,
        body: buildQuoteRequestBody(currentQuote, items)
      });
    },

    onMutate: async ({ productId }) => {
      await cache.cancel();

      const previousQuote = cache.getQuote();

      // Mark specific product as fetching for loading state
      cache.setQuote(old => {
        if (!old) return old;
        return {
          ...old,
          items: markProductForPricing(old.items, productId)
        };
      });

      return { previousQuote };
    },

    onError: (_error, _variables, context) => {
      // Rollback to previous state
      if (context?.previousQuote) {
        cache.setQuote(() => context.previousQuote);
      }
    },

    onSuccess: () => {
      // Skip refetch if there are queued mutations to preserve optimistic updates
      if (hasQueuedMutations()) {
        // Defer refetch until all queued mutations complete
        setOnQueueEmpty(() => cache.refetch());
      } else {
        // No queued mutations, refetch immediately
        cache.refetch();
      }
    }
  });
};
